import { createServerFn } from "@tanstack/react-start"
import { getRequest } from "@tanstack/react-start/server"
import { and, count, desc, eq, inArray, notInArray, sql, type SQL } from "drizzle-orm"
import { db } from "@/db/db"
import { fixtureValidationRuns } from "@/db/schema"
import { computeEvolution } from "@/features/fixture-detection-validation/server/fixtureValidationService"
import {
  fixtureValidationQuerySchema,
  type FixtureValidationCounts,
  type FixtureValidationEvolution,
  type FixtureValidationFilters,
  type FixtureValidationResultsResponse,
  type FixtureValidationRow,
  type FixtureValidationSummary,
} from "@/features/fixture-detection-validation/types/fixtureValidationTypes"
import { PermissionError, assertPermission } from "@/lib/assert-permission.server"
import { authFunctionMiddleware } from "@/lib/auth-middleware"

const FIXTURE_VALIDATION_STORAGE_WARNING =
  "Fixture validation storage is not available yet in this environment. Run the latest database migrations to enable results."

type FixtureValidationQueryRow = {
  id: number
  projectId: string
  projectName: string
  floorPlanReference: string
  sourceFilename: string | null
  status: FixtureValidationRow["validationStatus"]
  failureReason: string | null
  expectedCounts: FixtureValidationRow["expected"]
  detectedCounts: FixtureValidationRow["detected"]
  createdAt: Date
}

function mapSummary(summary: {
  total: number
  passed: number
  failed: number
}): FixtureValidationSummary {
  return {
    total: summary.total,
    passed: summary.passed,
    failed: summary.failed,
  }
}

function mapRow(
  row: FixtureValidationQueryRow,
  evolution: FixtureValidationEvolution,
): FixtureValidationRow {
  return {
    id: row.id,
    projectId: row.projectId,
    projectName: row.projectName,
    floorPlanReference: row.floorPlanReference,
    sourceFilename: row.sourceFilename,
    validationStatus: row.status,
    validationReason: row.failureReason,
    expected: row.expectedCounts,
    detected: row.detectedCounts,
    createdAt: row.createdAt.toISOString(),
    evolution,
  }
}

function createUnavailableResultsResponse(): FixtureValidationResultsResponse {
  return {
    success: true,
    summary: {
      total: 0,
      passed: 0,
      failed: 0,
    },
    totalCount: 0,
    results: [],
    warning: FIXTURE_VALIDATION_STORAGE_WARNING,
  }
}

function isFixtureValidationStorageUnavailableError(error: unknown) {
  const errorMessage =
    error instanceof Error
      ? `${error.message} ${error.cause instanceof Error ? error.cause.message : ""}`.toLowerCase()
      : ""
  const errorCode =
    typeof error === "object" &&
    error !== null &&
    "cause" in error &&
    typeof error.cause === "object" &&
    error.cause !== null &&
    "code" in error.cause &&
    typeof error.cause.code === "string"
      ? error.cause.code
      : typeof error === "object" &&
          error !== null &&
          "code" in error &&
          typeof error.code === "string"
        ? error.code
        : null

  return (
    errorCode === "42P01" ||
    errorCode === "42703" ||
    errorCode === "42704" ||
    errorMessage.includes("fixture_validation_runs") ||
    errorMessage.includes("fixture_validation_status") ||
    errorMessage.includes("fixture_validation_trigger_source") ||
    errorMessage.includes("does not exist")
  )
}

type PrevRunEntry = { expected: FixtureValidationCounts; detected: FixtureValidationCounts }

async function fetchPreviousRunsMap(
  rows: Array<{ id: number; projectId: string }>,
): Promise<Map<string, PrevRunEntry>> {
  const map = new Map<string, PrevRunEntry>()
  if (rows.length === 0) return map

  const latestIds = rows.map((r) => r.id)
  const projectIds = rows.map((r) => r.projectId)

  const prevIdSubquery = db
    .select({
      projectId: fixtureValidationRuns.projectId,
      prevId: sql<number>`max(${fixtureValidationRuns.id})`.as("prev_id"),
    })
    .from(fixtureValidationRuns)
    .where(
      and(
        inArray(fixtureValidationRuns.projectId, projectIds),
        notInArray(fixtureValidationRuns.id, latestIds),
      ),
    )
    .groupBy(fixtureValidationRuns.projectId)
    .as("prev_ids")

  const prevRuns = await db
    .select({
      projectId: fixtureValidationRuns.projectId,
      expectedCounts: fixtureValidationRuns.expectedCounts,
      detectedCounts: fixtureValidationRuns.detectedCounts,
    })
    .from(fixtureValidationRuns)
    .innerJoin(
      prevIdSubquery,
      and(
        eq(fixtureValidationRuns.projectId, prevIdSubquery.projectId),
        eq(fixtureValidationRuns.id, prevIdSubquery.prevId),
      ),
    )

  for (const prev of prevRuns) {
    map.set(prev.projectId, {
      expected: prev.expectedCounts as FixtureValidationCounts,
      detected: prev.detectedCounts as FixtureValidationCounts,
    })
  }
  return map
}

async function queryFixtureValidationResults(filters: FixtureValidationFilters) {
  const latestProjectIdsQuery = db
    .select({
      projectId: fixtureValidationRuns.projectId,
      latestId: sql<number>`max(${fixtureValidationRuns.id})`.as("latest_id"),
    })
    .from(fixtureValidationRuns)
    .groupBy(fixtureValidationRuns.projectId)
    .as("latest_fixture_validation_runs")

  const latestRowsBase = db
    .select({
      id: fixtureValidationRuns.id,
      projectId: fixtureValidationRuns.projectId,
      projectName: fixtureValidationRuns.projectName,
      floorPlanReference: fixtureValidationRuns.floorPlanReference,
      sourceFilename: fixtureValidationRuns.sourceFilename,
      status: fixtureValidationRuns.status,
      failureReason: fixtureValidationRuns.failureReason,
      expectedCounts: fixtureValidationRuns.expectedCounts,
      detectedCounts: fixtureValidationRuns.detectedCounts,
      createdAt: fixtureValidationRuns.createdAt,
    })
    .from(fixtureValidationRuns)
    .innerJoin(
      latestProjectIdsQuery,
      and(
        eq(fixtureValidationRuns.projectId, latestProjectIdsQuery.projectId),
        eq(fixtureValidationRuns.id, latestProjectIdsQuery.latestId),
      ),
    )

  const latestRows = latestRowsBase.as("latest_fixture_validation_rows")
  const whereConditions: SQL[] = []

  if (filters.status) {
    whereConditions.push(eq(latestRows.status, filters.status))
  }

  if (filters.floorPlanRef) {
    const escaped = filters.floorPlanRef.replace(/[\\%_]/g, "\\$&")
    const pattern = `%${escaped}%`
    whereConditions.push(
      sql`(
        ${latestRows.floorPlanReference} ilike ${pattern} escape '\'
        or ${latestRows.projectName} ilike ${pattern} escape '\'
        or coalesce(${latestRows.sourceFilename}, '') ilike ${pattern} escape '\'
      )`,
    )
  }

  const whereClause = whereConditions.length > 0 ? and(...whereConditions) : undefined

  const summaryRows = await db
    .select({
      total: count(),
      passed: sql<number>`sum(case when ${latestRows.status} = 'pass' then 1 else 0 end)`.mapWith(
        Number,
      ),
      failed: sql<number>`sum(case when ${latestRows.status} = 'fail' then 1 else 0 end)`.mapWith(
        Number,
      ),
    })
    .from(latestRows)

  const totalCountRows = await db.select({ count: count() }).from(latestRows).where(whereClause)

  const rows = await db
    .select()
    .from(latestRows)
    .where(whereClause)
    .orderBy(
      sql`case when ${latestRows.status} = 'fail' then 0 else 1 end`,
      desc(latestRows.createdAt),
      latestRows.floorPlanReference,
    )
    .limit(filters.pageSize)
    .offset(filters.pageIndex * filters.pageSize)

  const typedRows = rows.map((row) => ({
    ...row,
    expectedCounts: row.expectedCounts as FixtureValidationRow["expected"],
    detectedCounts: row.detectedCounts as FixtureValidationRow["detected"],
  }))

  const prevRunsMap = await fetchPreviousRunsMap(typedRows)

  return {
    summary: mapSummary({
      total: summaryRows[0]?.total ?? 0,
      passed: summaryRows[0]?.passed ?? 0,
      failed: summaryRows[0]?.failed ?? 0,
    }),
    totalCount: totalCountRows[0]?.count ?? 0,
    results: typedRows.map((row) =>
      mapRow(
        row,
        computeEvolution(
          { expected: row.expectedCounts, detected: row.detectedCounts },
          prevRunsMap.get(row.projectId),
        ),
      ),
    ),
  }
}

export const getFixtureValidationResults = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .inputValidator((filters: unknown) => fixtureValidationQuerySchema.parse(filters))
  .handler(async ({ data }): Promise<FixtureValidationResultsResponse> => {
    const request = getRequest()

    try {
      await assertPermission(request, "fixtureValidation", "read")
    } catch (error) {
      if (error instanceof PermissionError) {
        return {
          success: false,
          error: error.message,
          code: error.code,
        }
      }
      throw error
    }

    let result
    try {
      result = await queryFixtureValidationResults(data)
    } catch (error) {
      if (isFixtureValidationStorageUnavailableError(error)) {
        return createUnavailableResultsResponse()
      }
      throw error
    }

    return {
      success: true,
      ...result,
    }
  })
